
import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Calendar, Zap, Target, Clock, Users, BarChart3, Sparkles, Download, Focus, ChevronDown, ChevronUp } from 'lucide-react';
import { prioritizeTasks, exportICS, type Task, type ScoredTask } from './lib/engine';
import { aiOptimizeDay } from './lib/ai-fallback';
import { SettingsButton } from './lib/SettingsButton';

export default function App() {
  const [tasks, setTasks] = useState<Task[]>(() => JSON.parse(localStorage.getItem('ca_tasks') || '[]'));
  const [scored, setScored] = useState<ScoredTask[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState('');
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const [history, setHistory] = useState<any[]>(() => JSON.parse(localStorage.getItem('ca_task_history') || '[]'));

  useEffect(() => { localStorage.setItem('ca_tasks', JSON.stringify(tasks)); }, [tasks]);

  const addTask = (t: Task) => { setTasks(prev => [...prev, t]); setShowForm(false); };
  const removeTask = (id: string) => setTasks(prev => prev.filter(t => t.id !== id));
  const runPrioritization = () => { const s = prioritizeTasks(tasks); setScored(s); };
  const handleAiOptimize = async () => { if (scored.length === 0) return; setIsAiLoading(true); setAiSuggestion(''); await aiOptimizeDay(scored, c => setAiSuggestion(p => p + c)); setIsAiLoading(false); };
  const exportCalendar = () => { const ics = exportICS(scored); const b = new Blob([ics], { type: 'text/calendar' }); const u = URL.createObjectURL(b); const a = document.createElement('a'); a.href = u; a.download = 'catalystagile-priority.ics'; a.click(); };
  const saveToHistory = () => { const entry = { date: new Date().toLocaleDateString(), tasks: scored.length, topTask: scored[0]?.title }; const h = [entry, ...history].slice(0, 30); setHistory(h); localStorage.setItem('ca_task_history', JSON.stringify(h)); };

  const quickAdd = (text: string) => {
    const t = text.toLowerCase();
    const urgency = /\b(urgent|asap|emergency|critical|deadline|today)\b/.test(t) ? 5 : /\b(soon|this week|tomorrow)\b/.test(t) ? 4 : /\b(next week|later)\b/.test(t) ? 3 : 2;
    const importance = /\b(important|critical|key|main|primary|essential)\b/.test(t) ? 5 : /\b(needed|required|should)\b/.test(t) ? 4 : 3;
    const impact = /\b(revenue|client|launch|release|demo|presentation)\b/.test(t) ? 3 : /\b(improve|enhance|optimize)\b/.test(t) ? 2 : 1;
    const effort = /\b(quick|small|easy|5 min|15 min|30 min)\b/.test(t) ? 1 : /\b(big|large|complex|project|week|month)\b/.test(t) ? 8 : 4;
    addTask({ id: Date.now().toString(), title: text, urgency, importance, impact, confidence: 80, effort, reach: 5, notes: '' });
  };

  return (
    <div className="min-h-screen bg-[#050b14] text-[#f0f0f0]">
      <header className="border-b border-white/10 bg-[#0a1628] sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#d4af37] flex items-center justify-center text-[#0a1628] font-bold text-lg">C</div>
            <div>
              <h1 className="text-lg font-semibold tracking-tight text-[#f0f0f0]">CatalystAgile <span className="text-[#d4af37]">Daily Prioritizer</span></h1>
              <p className="text-xs text-[#8a9bb8]">Eisenhower + RICE + MoSCoW — Works Offline</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setFocusMode(!focusMode)} className={`px-3 py-1.5 text-xs rounded-md transition-colors flex items-center gap-1.5 ${focusMode ? 'bg-[#d4af37] text-[#0a1628]' : 'bg-[#132238] text-[#8a9bb8] hover:bg-[#1a2d4a] border border-white/10'}`}>
              <Focus className="w-3.5 h-3.5"/> {focusMode ? 'Exit Focus' : 'Focus Mode'}
            </button>
            {scored.length > 0 && (
              <>
                <button onClick={exportCalendar} className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 text-[#8a9bb8] transition-colors flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5"/> Export ICS
                </button>
                <button onClick={handleAiOptimize} disabled={isAiLoading} className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-[#d4af37]/30 text-[#d4af37] transition-colors flex items-center gap-1.5 disabled:opacity-50">
                  <Sparkles className="w-3.5 h-3.5"/> {isAiLoading ? 'Optimizing...' : 'AI Optimize'}
                </button>
              </>
            )}
            <SettingsButton className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 inline-flex items-center gap-1.5 transition-colors text-[#8a9bb8] hover:text-[#d4af37]" />
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* Quick Add */}
        {!showForm && (
          <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4 mb-4">
            <form onSubmit={e => { e.preventDefault(); const input = (e.target as any).elements.quick.value; if (input.trim()) quickAdd(input); (e.target as any).reset(); }} className="flex gap-2">
              <input name="quick" placeholder='Quick add: "urgent client proposal 2hrs" — auto-parses priority...' className="flex-1 bg-[#070f1a] border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#d4af37]/50"/>
              <button type="submit" className="px-4 py-2 rounded-lg bg-[#d4af37] text-[#0a1628] font-semibold text-sm hover:bg-[#e8c547] transition-colors flex items-center gap-1.5"><Plus className="w-4 h-4"/> Add</button>
              <button type="button" onClick={() => setShowForm(true)} className="px-3 py-2 rounded-lg bg-[#132238] border border-white/10 text-[#8a9bb8] hover:text-[#f0f0f0] text-sm transition-colors">Detailed</button>
            </form>
          </div>
        )}

        {/* Detailed Form */}
        {showForm && (
          <TaskForm onAdd={addTask} onCancel={() => setShowForm(false)}/>
        )}

        {/* Task List */}
        {tasks.length > 0 && !focusMode && (
          <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-[#f0f0f0]">Tasks ({tasks.length})</h2>
              <button onClick={runPrioritization} className="px-4 py-2 rounded-lg bg-[#d4af37] text-[#0a1628] font-semibold text-sm hover:bg-[#e8c547] transition-colors flex items-center gap-1.5">
                <Zap className="w-4 h-4"/> Prioritize
              </button>
            </div>
            <div className="space-y-2">
              {tasks.map(t => (
                <div key={t.id} className="flex items-center gap-3 bg-[#132238] rounded-lg p-3 border border-white/5">
                  <div className="flex-1"><div className="text-sm font-medium text-[#f0f0f0]">{t.title}</div><div className="text-xs text-[#8a9bb8] mt-0.5">U{t.urgency} · I{t.importance} · E{t.effort}h · Im{t.impact}</div></div>
                  <button onClick={() => removeTask(t.id)} className="p-1.5 rounded text-[#8a9bb8] hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4"/></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Scored Results */}
        {scored.length > 0 && (
          <div className="space-y-4">
            {/* AI Suggestion */}
            {aiSuggestion && (
              <div className="bg-[#0f1d32] rounded-xl border border-[#d4af37]/20 p-4">
                <h3 className="text-sm font-semibold text-[#d4af37] mb-2 flex items-center gap-2"><Sparkles className="w-4 h-4"/> AI Optimization Suggestions</h3>
                <div className="text-sm text-[#f0f0f0] whitespace-pre-wrap leading-relaxed">{aiSuggestion}</div>
              </div>
            )}

            {/* Focus Mode — Top 3 Only */}
            {focusMode ? (
              <div className="bg-[#0f1d32] rounded-xl border border-[#d4af37]/30 p-6">
                <h2 className="text-lg font-semibold text-[#d4af37] mb-6 text-center">Today's Focus — Top 3</h2>
                <div className="space-y-4">
                  {scored.slice(0, 3).map((t, i) => (
                    <div key={t.id} className="bg-[#070f1a] rounded-xl p-5 border-l-4" style={{ borderColor: t.color }}>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl font-bold" style={{ color: t.color }}>#{t.rank}</span>
                        <h3 className="text-lg font-semibold text-[#f0f0f0]">{t.title}</h3>
                      </div>
                      <p className="text-sm text-[#8a9bb8] mb-3">{t.reasoning}</p>
                      <div className="flex items-center gap-4 text-xs">
                        <span className="px-2 py-1 rounded bg-[#132238] text-[#f0f0f0]">{t.eisenhower.quadrant.replace('_', ' ')}</span>
                        <span className="px-2 py-1 rounded bg-[#132238] text-[#f0f0f0]">{t.timeBlock}</span>
                        <span className="text-[#d4af37] font-mono">Score: {t.combinedScore}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {/* Full Ranked List */}
                <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4">
                  <h2 className="text-sm font-semibold text-[#f0f0f0] mb-4">Prioritized Tasks</h2>
                  <div className="space-y-2">
                    {scored.map(t => (
                      <div key={t.id} className="bg-[#132238] rounded-lg p-4 border-l-2 transition-all hover:bg-[#1a2d4a]" style={{ borderColor: t.color }}>
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-lg font-bold w-6" style={{ color: t.color }}>{t.rank}</span>
                            <div>
                              <div className="text-sm font-medium text-[#f0f0f0]">{t.title}</div>
                              <div className="text-xs text-[#8a9bb8] mt-1">{t.reasoning}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-bold" style={{ color: t.color }}>{t.combinedScore}</div>
                            <div className="text-[10px] text-[#8a9bb8]">combined</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 mt-3 text-[10px] uppercase tracking-wider">
                          <span className="px-2 py-1 rounded bg-[#070f1a] text-[#f0f0f0]">{t.eisenhower.quadrant.replace('_', ' ')}</span>
                          <span className="px-2 py-1 rounded bg-[#070f1a] text-[#f0f0f0]">{t.moscow.replace('_', ' ')}</span>
                          <span className="px-2 py-1 rounded bg-[#070f1a] text-[#f0f0f0]">{t.timeBlock}</span>
                          <span className="text-[#8a9bb8]">RICE: {t.rice.score}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Eisenhower Matrix */}
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: 'DO FIRST', q: 'DO_FIRST', color: '#ef4444', desc: 'Urgent + Important' },
                    { label: 'SCHEDULE', q: 'SCHEDULE', color: '#f59e0b', desc: 'Important, Not Urgent' },
                    { label: 'DELEGATE', q: 'DELEGATE', color: '#3b82f6', desc: 'Urgent, Not Important' },
                    { label: 'ELIMINATE', q: 'ELIMINATE', color: '#6b7280', desc: 'Neither' }
                  ].map(q => (
                    <div key={q.q} className="bg-[#0f1d32] rounded-xl border border-white/10 p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: q.color }}/>
                        <span className="text-xs font-semibold text-[#f0f0f0]">{q.label}</span>
                        <span className="text-[10px] text-[#8a9bb8]">{q.desc}</span>
                      </div>
                      <div className="space-y-1">
                        {scored.filter(t => t.eisenhower.quadrant === q.q).map(t => (
                          <div key={t.id} className="text-xs text-[#f0f0f0] bg-[#132238] rounded px-2 py-1.5 truncate">{t.rank}. {t.title}</div>
                        ))}
                        {scored.filter(t => t.eisenhower.quadrant === q.q).length === 0 && <div className="text-xs text-[#8a9bb8] italic">No tasks</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

// Canonical RICE Impact tiers — Impact is a fixed scale, not a continuous range.
const RICE_IMPACT = [
  { v: 0.25, l: 'Minimal (0.25)' },
  { v: 0.5, l: 'Low (0.5)' },
  { v: 1, l: 'Medium (1)' },
  { v: 2, l: 'High (2)' },
  { v: 3, l: 'Massive (3)' },
];

function TaskForm({ onAdd, onCancel }: { onAdd: (t: Task) => void; onCancel: () => void }) {
  const [form, setForm] = useState({ title: '', urgency: 3, importance: 3, impact: 2, confidence: 80, effort: 3, reach: 5, notes: '' });
  return (
    <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4 mb-4">
      <h2 className="text-sm font-semibold text-[#d4af37] mb-4">Add Task Details</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div className="md:col-span-2"><label className="text-xs text-[#8a9bb8] mb-1 block">Title</label><input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full bg-[#070f1a] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#d4af37]/50"/></div>
        {[{ k: 'urgency', l: 'Urgency (1-5)', max: 5 }, { k: 'importance', l: 'Importance (1-5)', max: 5 }, { k: 'impact', l: 'Impact (RICE)', max: 3, step: 0.25 }, { k: 'confidence', l: 'Confidence % (10-100)', max: 100 }, { k: 'effort', l: 'Effort hours (1-10)', max: 10 }, { k: 'reach', l: 'Reach / People affected (1-10)', max: 10 }].map((f: any) => (
          <div key={f.k}>
            <label className="text-xs text-[#8a9bb8] mb-1 block">{f.l}: <span className="text-[#d4af37]">{form[f.k as keyof typeof form]}</span></label>
            {f.k === 'impact' ? (
              <select
                value={form.impact}
                onChange={e => setForm({ ...form, impact: +e.target.value })}
                className="w-full bg-[#070f1a] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#d4af37]/50">
                {RICE_IMPACT.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            ) : (
              <input type="range" min={f.k === 'confidence' ? 10 : 1} max={f.max} step={f.step || 1}
                value={form[f.k as keyof typeof form]} onChange={e => setForm({ ...form, [f.k]: +e.target.value })}
                className="w-full accent-[#d4af37]"/>
            )}
          </div>
        ))}
        <div className="md:col-span-2"><label className="text-xs text-[#8a9bb8] mb-1 block">Notes</label><textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full bg-[#070f1a] border border-white/10 rounded-lg p-3 text-sm focus:outline-none focus:border-[#d4af37]/50 resize-none"/></div>
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-4 py-2 rounded-lg bg-[#132238] text-[#8a9bb8] text-sm hover:bg-[#1a2d4a] transition-colors">Cancel</button>
        <button onClick={() => { if (form.title.trim()) { onAdd({ ...form, id: Date.now().toString() }); } }} className="px-4 py-2 rounded-lg bg-[#d4af37] text-[#0a1628] font-semibold text-sm hover:bg-[#e8c547] transition-colors">Add Task</button>
      </div>
    </div>
  );
}
